import React, { useState } from 'react';
import { maquetaService, storageService } from '../../services/api';

/**
 * EditorMultimedia — formulario del bloque de audio o video dentro de la
 * maqueta. Sube el archivo al bucket del periódico y guarda en el bloque los
 * datos de la pieza.
 *
 * Los límites de formato y peso los valida el servidor (ver MULTIMEDIA.md); si
 * rechaza el archivo, el mensaje de error trae el comando de ffmpeg para
 * convertirlo, y se muestra tal cual al editor.
 */
export default function EditorMultimedia({ bloque, edicionId, onCerrar, onGuardado }) {
  const inicial = (() => {
    try {
      const d = JSON.parse(bloque.elementoData || '{}');
      return d.parametros || {};
    } catch { return {}; }
  })();

  const [tipo, setTipo] = useState(inicial.tipo || 'audio');
  const [titulo, setTitulo] = useState(inicial.titulo || '');
  const [autoria, setAutoria] = useState(inicial.autoria || '');
  const [url, setUrl] = useState(inicial.url || '');
  const [gsUri, setGsUri] = useState(inicial.gsUri || '');
  const [duracion, setDuracion] = useState(inicial.duracionSegundos || 0);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState(null);

  // La duración se lee del propio archivo: pedírsela al editor es una fuente
  // de errores y el navegador ya la sabe.
  const leerDuracion = (archivo) => new Promise((resolve) => {
    try {
      const el = document.createElement(tipo === 'video' ? 'video' : 'audio');
      el.preload = 'metadata';
      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        resolve(Number.isFinite(el.duration) ? Math.round(el.duration) : 0);
      };
      el.onerror = () => resolve(0);
      el.src = URL.createObjectURL(archivo);
    } catch { resolve(0); }
  });

  const subir = async (ev) => {
    const archivo = ev.target.files?.[0];
    if (!archivo) return;
    setError(null);
    setSubiendo(true);
    try {
      const segundos = await leerDuracion(archivo);
      const r = await storageService.subirMultimedia(edicionId, archivo, tipo);
      setGsUri(r.gsUri);
      if (segundos > 0) setDuracion(segundos);
      if (!titulo) setTitulo(archivo.name.replace(/\.[^.]+$/, ''));
    } catch (e) {
      setError(e.message);
    } finally {
      setSubiendo(false);
      ev.target.value = '';
    }
  };

  const guardar = async () => {
    setError(null);
    try {
      const elementoData = JSON.stringify({
        tipoContenido: 'multimedia',
        parametros: {
          tipo, titulo, autoria, gsUri, url,
          duracionSegundos: Number(duracion) || 0,
        },
      });
      const actualizado = await maquetaService.actualizarBloque(bloque.id, {
        columnaInicio: bloque.columnaInicio, filaInicio: bloque.filaInicio,
        columnasSpan: bloque.columnasSpan, filasSpan: bloque.filasSpan,
        articuloId: bloque.articuloId ?? null, pautaId: bloque.pautaId ?? null,
        elementoData, estilo: bloque.estilo ?? null,
        plantillaArticuloId: bloque.plantillaArticuloId ?? null,
      });
      onGuardado?.(actualizado);
      onCerrar?.();
    } catch (e) {
      setError(e.message);
    }
  };

  const campo = { width:'100%', fontSize:12, padding:'5px 7px', border:'1px solid #ddd',
                  borderRadius:4, marginBottom:8, boxSizing:'border-box' };
  const etiqueta = { fontSize:11, fontWeight:600, color:'#57534e', display:'block', marginBottom:2 };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center',
    }} onMouseDown={onCerrar}>
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{ background:'#fff', borderRadius:8, padding:20, width:440, maxWidth:'92vw',
                 maxHeight:'88vh', overflowY:'auto', boxShadow:'0 12px 40px rgba(0,0,0,.3)' }}
      >
        <h3 style={{ margin:'0 0 14px', fontSize:16 }}>Audio o video</h3>

        <label style={etiqueta}>Tipo de pieza</label>
        <select value={tipo} onChange={e => setTipo(e.target.value)} style={campo}>
          <option value="audio">Audio — entrevista, declaración</option>
          <option value="video">Video — cobertura, testimonio</option>
        </select>

        <label style={etiqueta}>Título</label>
        <input value={titulo} onChange={e => setTitulo(e.target.value)} style={campo}
               placeholder="Entrevista a Deny Medina" />

        <label style={etiqueta}>Autoría o entrevistado</label>
        <input value={autoria} onChange={e => setAutoria(e.target.value)} style={campo}
               placeholder="Por Javier Gil Carmona" />

        <label style={etiqueta}>Archivo</label>
        <div style={{ border:'1px dashed #c9a227', borderRadius:6, padding:10, marginBottom:8,
                      background:'#fffdf6' }}>
          {gsUri ? (
            <div style={{ fontSize:11, color:'#166534', wordBreak:'break-all', marginBottom:6 }}>
              ✓ {gsUri}
            </div>
          ) : (
            <div style={{ fontSize:11, color:'#92400e', marginBottom:6 }}>
              {tipo === 'video'
                ? 'MP4 H.264, 720p, máx. 5 min y 120 MB, con faststart.'
                : 'MP3 96 kbps mono, máx. 45 min y 40 MB.'}
            </div>
          )}
          <input type="file" disabled={subiendo}
                 accept={tipo === 'video' ? 'video/mp4' : 'audio/mpeg'}
                 onChange={subir} style={{ fontSize:11 }} />
          {subiendo && <div style={{ fontSize:11, color:'#57534e', marginTop:4 }}>Subiendo…</div>}
        </div>

        <label style={etiqueta}>Duración (segundos)</label>
        <input type="number" value={duracion} onChange={e => setDuracion(e.target.value)}
               style={campo} min={0} />

        <label style={etiqueta}>Enlace para el código QR</label>
        <input value={url} onChange={e => setUrl(e.target.value)} style={campo}
               placeholder="https://news.fenagro-oficial.org/..." />
        <div style={{ fontSize:10, color:'#78716c', marginTop:-4, marginBottom:10 }}>
          Es la dirección a la que llega quien escanee el QR impreso. Si se deja
          vacía, el QR apunta al archivo del bucket.
        </div>

        {error && (
          <div style={{ background:'#fef2f2', border:'1px solid #fecaca', color:'#991b1b',
                        fontSize:11, padding:8, borderRadius:4, marginBottom:10,
                        whiteSpace:'pre-wrap' }}>
            {error}
          </div>
        )}

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button onClick={onCerrar}
                  style={{ fontSize:12, padding:'6px 12px', border:'1px solid #ddd',
                           background:'#fff', borderRadius:4, cursor:'pointer' }}>
            Cancelar
          </button>
          <button onClick={guardar} disabled={subiendo}
                  style={{ fontSize:12, padding:'6px 12px', border:'none', background:'#166534',
                           color:'#fff', borderRadius:4, cursor:'pointer', fontWeight:600 }}>
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
